import React, { useState, useMemo, useEffect } from 'react';
import { useFavoritesContext } from '../context/FavoritesContext';
import { FiArrowLeft, FiCheck, FiX, FiFilter, FiInfo } from 'react-icons/fi';
import { AiOutlineHeart, AiFillHeart } from 'react-icons/ai';
import { ImSpinner2 } from 'react-icons/im';
import { BsSortAlphaDown } from 'react-icons/bs';
import { checkDomainsMultipleTLDs } from '../services/api';
import { DomainAvailability } from '../types/NameResult';

interface FavoritesViewProps {
  onBackToResults: () => void;
  onCheckDomains?: () => void;
}

const TLDS = ['.com', '.io', '.ai', '.co', '.app'];

type SortOption = 'recent' | 'alphabetical' | 'shortlisted';
type FilterOption = 'all' | 'shortlisted' | 'not-shortlisted';

const TAG_SUGGESTIONS = [
  'Top Pick',
  'Creative',
  'Professional',
  'Modern',
  'Playful',
  'Memorable',
  'Short',
  'Brandable',
  'Tech',
  'Minimal',
];

export const FavoritesView: React.FC<FavoritesViewProps> = ({
  onBackToResults,
  onCheckDomains = () => {},
}) => {
  const {
    favorites,
    toggleFavorite,
    toggleShortlist,
    updateNotes,
    updateTags,
    getMetadata,
    getShortlisted,
  } = useFavoritesContext();

  const [expandedName, setExpandedName] = useState<string | null>(null);
  const [domainResults, setDomainResults] = useState<
    Record<string, DomainAvailability>
  >({});
  const [checkingDomains, setCheckingDomains] = useState(false);
  const [sortBy, setSortBy] = useState<SortOption>('recent');
  const [filterBy, setFilterBy] = useState<FilterOption>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [activeTagInput, setActiveTagInput] = useState<string | null>(null);
  const [tagInputValue, setTagInputValue] = useState('');
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);

  const shortlistedNames = getShortlisted();

  // Sort and filter favorites
  const sortedAndFilteredFavorites = useMemo(() => {
    let filtered = [...favorites];

    // Apply filter
    if (filterBy === 'shortlisted') {
      filtered = filtered.filter((name) => getMetadata(name)?.isShortlisted);
    } else if (filterBy === 'not-shortlisted') {
      filtered = filtered.filter((name) => !getMetadata(name)?.isShortlisted);
    }

    // Apply sort
    if (sortBy === 'alphabetical') {
      filtered.sort((a, b) => a.localeCompare(b));
    } else if (sortBy === 'shortlisted') {
      filtered.sort((a, b) => {
        const aShortlisted = getMetadata(a)?.isShortlisted || false;
        const bShortlisted = getMetadata(b)?.isShortlisted || false;
        if (aShortlisted === bShortlisted) return 0;
        return aShortlisted ? -1 : 1;
      });
    } else if (sortBy === 'recent') {
      filtered.sort((a, b) => {
        const aTime = getMetadata(a)?.addedAt || 0;
        const bTime = getMetadata(b)?.addedAt || 0;
        return bTime - aTime;
      });
    }

    return filtered;
  }, [favorites, sortBy, filterBy, getMetadata]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      const currentList = sortedAndFilteredFavorites;
      if (currentList.length === 0) return;

      switch (e.key) {
        case 'j': // Move down
          e.preventDefault();
          setFocusedIndex((prev) =>
            prev < currentList.length - 1 ? prev + 1 : prev,
          );
          break;
        case 'k': // Move up
          e.preventDefault();
          setFocusedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case 's': // Toggle shortlist for focused item
          e.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < currentList.length) {
            toggleShortlist(currentList[focusedIndex]);
          }
          break;
        case 'x': // Remove from favorites
          e.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < currentList.length) {
            toggleFavorite(currentList[focusedIndex]);
            // Adjust focus after deletion
            if (focusedIndex >= currentList.length - 1) {
              setFocusedIndex(Math.max(0, focusedIndex - 1));
            }
          }
          break;
        case 'f': // Toggle filters
          e.preventDefault();
          setShowFilters((prev) => !prev);
          break;
        case '?': // Show keyboard help
          e.preventDefault();
          setShowKeyboardHelp((prev) => !prev);
          break;
        case 'Escape': // Clear focus and close help
          setFocusedIndex(-1);
          setShowKeyboardHelp(false);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    focusedIndex,
    sortedAndFilteredFavorites,
    toggleShortlist,
    toggleFavorite,
  ]);

  const handleCheckDomains = async () => {
    if (shortlistedNames.length === 0) return;

    setCheckingDomains(true);
    onCheckDomains(); // Trigger stage change

    try {
      const results: Record<string, DomainAvailability> = {};

      for (const name of shortlistedNames) {
        const res = await checkDomainsMultipleTLDs({ name, tlds: TLDS });
        results[name] = res.data;
      }

      setDomainResults(results);
    } catch (err) {
      console.error('Domain check failed:', err);
    } finally {
      setCheckingDomains(false);
    }
  };

  const handleNotesChange = (name: string, notes: string) => {
    updateNotes(name, notes);
  };

  const handleAddTag = (name: string, tag: string) => {
    const metadata = getMetadata(name);
    const currentTags = metadata?.tags || [];
    if (!currentTags.includes(tag)) {
      updateTags(name, [...currentTags, tag]);
    }
  };

  const handleRemoveTag = (name: string, tagToRemove: string) => {
    const metadata = getMetadata(name);
    const currentTags = metadata?.tags || [];
    updateTags(
      name,
      currentTags.filter((tag) => tag !== tagToRemove),
    );
  };

  const handleTagInputKeyDown = (
    name: string,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'Enter' && tagInputValue.trim()) {
      e.preventDefault();
      handleAddTag(name, tagInputValue.trim());
      setTagInputValue('');
    } else if (e.key === 'Escape') {
      setActiveTagInput(null);
      setTagInputValue('');
    }
  };

  const toggleExpand = (name: string) => {
    setExpandedName(expandedName === name ? null : name);
  };

  const handleBuyDomain = (domain: string) => {
    window.open(
      `https://www.namecheap.com/domains/registration/results/?domain=${domain}`,
      '_blank',
    );
  };

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4 sm:py-6">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={onBackToResults}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition"
          >
            <FiArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Back to Results</span>
          </button>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                Your Favorites
              </h1>
              <p className="text-gray-600 mt-1 sm:mt-2 text-sm sm:text-base">
                Review and shortlist your top picks
              </p>
            </div>
            {favorites.length > 0 && (
              <div className="flex gap-2">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center justify-center gap-2 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition font-medium text-sm whitespace-nowrap"
                >
                  <FiFilter className="w-4 h-4" />
                  {showFilters ? 'Hide' : 'Show'} Filters
                </button>
                <button
                  onClick={() => setShowKeyboardHelp(true)}
                  className="flex items-center justify-center px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
                  title="Keyboard shortcuts"
                >
                  <FiInfo className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filters Section */}
      {showFilters && favorites.length > 0 && (
        <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4">
          <div className="max-w-4xl mx-auto space-y-4">
            {/* Sort Options */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Sort by:
              </span>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSortBy('recent')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                    sortBy === 'recent'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Recent
                </button>
                <button
                  onClick={() => setSortBy('alphabetical')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition flex items-center gap-1 ${
                    sortBy === 'alphabetical'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <BsSortAlphaDown className="w-4 h-4" />
                  A-Z
                </button>
                <button
                  onClick={() => setSortBy('shortlisted')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition whitespace-nowrap ${
                    sortBy === 'shortlisted'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Shortlisted First
                </button>
              </div>
            </div>

            {/* Filter Options */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Show:
              </span>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setFilterBy('all')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                    filterBy === 'all'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setFilterBy('shortlisted')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition whitespace-nowrap ${
                    filterBy === 'shortlisted'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Shortlisted Only
                </button>
                <button
                  onClick={() => setFilterBy('not-shortlisted')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition whitespace-nowrap ${
                    filterBy === 'not-shortlisted'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Not Shortlisted
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {favorites.length === 0 ? (
          /* Empty State */
          <div className="text-center py-16">
            <div className="text-6xl mb-4">⭐</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              No favorites yet
            </h3>
            <p className="text-gray-500 mb-6">
              Save a few names first to review them here
            </p>
            <button
              onClick={onBackToResults}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
            >
              Go Back to Results
            </button>
          </div>
        ) : (
          <>
            {/* Shortlist CTA */}
            {shortlistedNames.length > 0 && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-900">
                      {shortlistedNames.length} name
                      {shortlistedNames.length !== 1 ? 's' : ''} shortlisted
                    </p>
                    <p className="text-xs text-blue-700 mt-1">
                      Ready to check domain availability
                    </p>
                  </div>
                  <button
                    onClick={handleCheckDomains}
                    disabled={checkingDomains}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm transition disabled:opacity-50 flex items-center gap-2"
                  >
                    {checkingDomains ? (
                      <>
                        <ImSpinner2 className="animate-spin" />
                        Checking...
                      </>
                    ) : (
                      'Check Domains'
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Stats */}
            <div className="mb-6 flex items-center gap-6 text-sm text-gray-600">
              <div>
                <span className="font-semibold text-gray-900">
                  {favorites.length}
                </span>{' '}
                favorite{favorites.length !== 1 ? 's' : ''}
              </div>
              {shortlistedNames.length > 0 && (
                <div>
                  <span className="font-semibold text-pink-600">
                    {shortlistedNames.length}
                  </span>{' '}
                  shortlisted
                </div>
              )}
              {sortedAndFilteredFavorites.length !== favorites.length && (
                <div className="text-blue-600">
                  Showing {sortedAndFilteredFavorites.length} of{' '}
                  {favorites.length}
                </div>
              )}
            </div>

            {/* Empty filtered state */}
            {sortedAndFilteredFavorites.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-4xl mb-3">🔍</div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  No names match your filters
                </h3>
                <p className="text-gray-500 mb-4">
                  Try adjusting your filters to see more results
                </p>
                <button
                  onClick={() => {
                    setFilterBy('all');
                    setSortBy('recent');
                  }}
                  className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                >
                  Reset Filters
                </button>
              </div>
            ) : (
              /* Favorites List */
              <div className="space-y-4">
                {sortedAndFilteredFavorites.map((name, idx) => {
                  const metadata = getMetadata(name);
                  const isShortlisted = metadata?.isShortlisted || false;
                  const isExpanded = expandedName === name;
                  const domains = domainResults[name];
                  const isFocused = focusedIndex === idx;

                  return (
                    <div
                      key={name}
                      className={`border rounded-xl p-5 transition-all bg-white ${
                        isShortlisted
                          ? 'border-pink-300 ring-2 ring-pink-100'
                          : 'border-gray-200'
                      } ${
                        isFocused ? 'ring-4 ring-blue-200 border-blue-400' : ''
                      }`}
                    >
                      {/* Name and Actions */}
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-xl font-semibold text-gray-900 break-all">
                          {name}
                        </h3>
                        <div className="flex items-center gap-2 ml-4">
                          {/* Shortlist Button */}
                          <button
                            onClick={() => toggleShortlist(name)}
                            className={`p-2 rounded-lg transition ${
                              isShortlisted
                                ? 'text-pink-600 bg-pink-100 hover:bg-pink-200'
                                : 'text-gray-400 hover:text-pink-600 hover:bg-pink-50'
                            }`}
                            title={
                              isShortlisted
                                ? 'Remove from shortlist'
                                : 'Add to shortlist'
                            }
                          >
                            {isShortlisted ? (
                              <AiFillHeart className="w-5 h-5" />
                            ) : (
                              <AiOutlineHeart className="w-5 h-5" />
                            )}
                          </button>

                          {/* Remove Button */}
                          <button
                            onClick={() => toggleFavorite(name)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                            title="Remove from favorites"
                          >
                            <FiX className="w-5 h-5" />
                          </button>
                        </div>
                      </div>

                      {/* Notes */}
                      <textarea
                        value={metadata?.notes || ''}
                        onChange={(e) =>
                          handleNotesChange(name, e.target.value)
                        }
                        placeholder="Add notes..."
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        rows={2}
                      />

                      {/* Tags Section */}
                      <div className="mt-3">
                        {/* Existing Tags */}
                        <div className="flex flex-wrap gap-2 mb-2">
                          {(metadata?.tags || []).map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium"
                            >
                              {tag}
                              <button
                                onClick={() => handleRemoveTag(name, tag)}
                                className="hover:text-blue-900"
                              >
                                <FiX className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>

                        {/* Tag Input */}
                        {activeTagInput === name ? (
                          <div className="flex gap-2 items-center">
                            <input
                              type="text"
                              value={tagInputValue}
                              onChange={(e) => setTagInputValue(e.target.value)}
                              onKeyDown={(e) => handleTagInputKeyDown(name, e)}
                              onBlur={() => {
                                if (tagInputValue.trim()) {
                                  handleAddTag(name, tagInputValue.trim());
                                }
                                setActiveTagInput(null);
                                setTagInputValue('');
                              }}
                              placeholder="Type a tag and press Enter"
                              autoFocus
                              className="flex-1 px-2 py-1 text-xs border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                        ) : (
                          <>
                            {/* Quick Tag Suggestions */}
                            <div className="flex flex-wrap gap-1.5">
                              {TAG_SUGGESTIONS.filter(
                                (tag) => !(metadata?.tags || []).includes(tag),
                              )
                                .slice(0, 4)
                                .map((tag) => (
                                  <button
                                    key={tag}
                                    onClick={() => handleAddTag(name, tag)}
                                    className="px-2 py-0.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition"
                                  >
                                    + {tag}
                                  </button>
                                ))}
                              <button
                                onClick={() => {
                                  setActiveTagInput(name);
                                  setTagInputValue('');
                                }}
                                className="px-2 py-0.5 text-xs text-blue-600 hover:text-blue-700 font-medium"
                              >
                                + Custom tag
                              </button>
                            </div>
                          </>
                        )}
                      </div>

                      {/* Domain Results */}
                      {domains && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <button
                            onClick={() => toggleExpand(name)}
                            className="text-sm font-medium text-blue-600 hover:text-blue-700 mb-2 flex items-center gap-1"
                          >
                            {isExpanded ? '▼' : '▶'} Domain Availability
                          </button>

                          {isExpanded && (
                            <div className="space-y-2 mt-2">
                              {TLDS.map((tld) => {
                                const available = domains[tld];
                                return (
                                  <div
                                    key={tld}
                                    className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                                  >
                                    <div className="flex items-center gap-2">
                                      <span className="font-mono text-sm text-gray-900">
                                        {name}
                                        {tld}
                                      </span>
                                    </div>

                                    {available === null ? (
                                      <span className="text-xs text-gray-500">
                                        Unknown
                                      </span>
                                    ) : available ? (
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs text-green-700 font-medium flex items-center gap-1">
                                          <FiCheck className="w-3 h-3" />
                                          Available
                                        </span>
                                        <button
                                          onClick={() =>
                                            handleBuyDomain(`${name}${tld}`)
                                          }
                                          className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded font-medium transition"
                                        >
                                          Buy
                                        </button>
                                      </div>
                                    ) : (
                                      <span className="text-xs text-red-700 font-medium flex items-center gap-1">
                                        <FiX className="w-3 h-3" />
                                        Taken
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Keyboard Shortcuts Help Modal */}
      {showKeyboardHelp && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4"
          onClick={() => setShowKeyboardHelp(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">
                Keyboard Shortcuts
              </h3>
              <button
                onClick={() => setShowKeyboardHelp(false)}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-gray-700">Navigate down</span>
                <kbd className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-sm font-mono">
                  j
                </kbd>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-gray-700">Navigate up</span>
                <kbd className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-sm font-mono">
                  k
                </kbd>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-gray-700">Toggle shortlist</span>
                <kbd className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-sm font-mono">
                  s
                </kbd>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-gray-700">Remove from favorites</span>
                <kbd className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-sm font-mono">
                  x
                </kbd>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-gray-700">Toggle filters</span>
                <kbd className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-sm font-mono">
                  f
                </kbd>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-gray-700">Show this help</span>
                <kbd className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-sm font-mono">
                  ?
                </kbd>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-gray-700">Clear focus / Close</span>
                <kbd className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-sm font-mono">
                  Esc
                </kbd>
              </div>
            </div>

            <div className="mt-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-800">
                💡 <strong>Pro tip:</strong> Use{' '}
                <kbd className="px-1 bg-white rounded">j</kbd> and{' '}
                <kbd className="px-1 bg-white rounded">k</kbd> to quickly
                navigate, then <kbd className="px-1 bg-white rounded">s</kbd> to
                shortlist your favorites!
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
